import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { Building2, Plus, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import CentroForm from './Form.jsx';
import CentroDetail from './Detail.jsx';
import { LIST_CENTROS, ACTIVAR_CENTRO } from './gql.js';

function flatten(nodes, parent = null, acc = []) {
  for (const node of nodes) {
    acc.push({ ...node, padreLabel: parent ? `${parent.codigo} · ${parent.nombre}` : null });
    if (node.hijos?.length) {
      flatten(node.hijos, node, acc);
    }
  }
  return acc;
}

export default function CentrosList() {
  const toast = useToast();
  const [empresaId, setEmpresaId] = useState('1');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('view');
  const [{ data, fetching }, reexecuteQuery] = useQuery({
    query: LIST_CENTROS,
    variables: { empresaId, search: search || null },
    pause: !empresaId,
  });
  const [, activarCentro] = useMutation(ACTIVAR_CENTRO);

  const tree = data?.listCentros ?? [];
  const flat = useMemo(() => flatten(tree), [tree]);
  const selectedCentro = flat.find((item) => item.id === selectedId) ?? null;

  const handleRefresh = () => {
    if (empresaId) {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }
  };

  const handleToggle = async (centro) => {
    try {
      const result = await activarCentro({ id: centro.id, activo: !centro.activo });
      if (result.error) {
        throw result.error;
      }
      toast.success(!centro.activo ? 'Centro activado' : 'Centro desactivado');
      handleRefresh();
    } catch (error) {
      toast.error(error.message ?? 'Error actualizando el centro');
    }
  };

  const filteredTree = useMemo(() => {
    if (!search) return tree;
    const query = search.toLowerCase();
    const filter = (nodes) =>
      nodes
        .map((node) => {
          const children = node.hijos ? filter(node.hijos) : [];
          const matches = `${node.codigo} ${node.nombre}`.toLowerCase().includes(query);
          if (matches || children.length) {
            return { ...node, hijos: children };
          }
          return null;
        })
        .filter(Boolean);
    return filter(tree);
  }, [tree, search]);

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.95)] pb-16">
      <div className="mx-auto max-w-[1400px] px-4 pb-10 pt-8">
        <PageHeader
          title="Centros de Costo"
          subtitle="Organizá los centros y subcentros por empresa"
          actions={[
            <button
              key="refresh"
              type="button"
              onClick={handleRefresh}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizar
            </button>,
            <button
              key="edit"
              type="button"
              onClick={() => {
                if (!selectedCentro) {
                  toast.info('Seleccioná un centro para editar');
                  return;
                }
                setMode('edit');
              }}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              Editar
            </button>,
            <button
              key="create"
              type="button"
              onClick={() => setMode('create')}
              className="flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-4 w-4" />
              Nuevo centro
            </button>,
          ]}
        />

        <div className="grid grid-cols-12 gap-6">
          <aside className="col-span-12 md:col-span-4 space-y-4">
            <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-4 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-[rgb(var(--fg))]">
                  <Building2 className="h-4 w-4 text-[rgb(var(--muted))]" />
                  Empresa
                </label>
                <input
                  type="number"
                  min="1"
                  value={empresaId}
                  onChange={(event) => setEmpresaId(event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  placeholder="ID de empresa"
                />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar centro"
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                />
              </div>
            </section>

            <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.75)] p-3 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
              {fetching ? (
                <div className="flex h-64 items-center justify-center text-sm text-[rgb(var(--muted))]">Cargando centros...</div>
              ) : !filteredTree.length ? (
                <div className="flex h-64 items-center justify-center text-sm text-[rgb(var(--muted))]">Sin resultados</div>
              ) : (
                <div className="max-h-[520px] overflow-auto">
                  <CentroTree
                    nodes={filteredTree}
                    selectedId={selectedId}
                    onSelect={(node) => {
                      setSelectedId(node.id);
                      setMode('view');
                    }}
                    onToggle={handleToggle}
                  />
                </div>
              )}
            </section>
          </aside>

          <main className="col-span-12 md:col-span-8 space-y-4">
            {mode === 'create' ? (
              <CentroForm
                empresaId={empresaId}
                centros={flat}
                onSuccess={() => {
                  setMode('view');
                  handleRefresh();
                }}
                onCancel={() => setMode('view')}
              />
            ) : mode === 'edit' && selectedCentro ? (
              <CentroForm
                centro={selectedCentro}
                empresaId={empresaId}
                centros={flat}
                onSuccess={() => {
                  setMode('view');
                  handleRefresh();
                }}
                onCancel={() => setMode('view')}
              />
            ) : (
              <CentroDetail centro={selectedCentro} />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function CentroTree({ nodes, selectedId, onSelect, onToggle, depth = 0 }) {
  if (!nodes?.length) return null;
  return (
    <ul className="space-y-1">
      {nodes.map((node) => (
        <CentroTreeNode
          key={node.id}
          node={node}
          depth={depth}
          selectedId={selectedId}
          onSelect={onSelect}
          onToggle={onToggle}
        />
      ))}
    </ul>
  );
}

function CentroTreeNode({ node, depth, selectedId, onSelect, onToggle }) {
  const [expanded, setExpanded] = useState(true);
  const isSelected = node.id === selectedId;
  const hasChildren = node.hijos?.length > 0;
  return (
    <li>
      <div
        className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
          isSelected
            ? 'bg-[rgba(var(--brand),0.15)] text-[rgb(var(--brand-700))]'
            : 'text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.12)]'
        }`}
        style={{ marginLeft: depth * 12 }}
      >
        <button
          type="button"
          onClick={() => {
            onSelect(node);
            if (hasChildren) {
              setExpanded((prev) => !prev);
            }
          }}
          className="flex flex-1 items-center gap-3 text-left focus:outline-none"
        >
          {hasChildren ? (
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full border border-[rgb(var(--border))] text-[10px] font-semibold ${
                expanded ? 'bg-[rgba(var(--brand),0.15)] text-[rgb(var(--brand-700))]' : 'text-[rgb(var(--muted))]'
              }`}
            >
              {expanded ? '-' : '+'}
            </span>
          ) : (
            <span className="h-5 w-5" aria-hidden />
          )}
          <div className="flex flex-col">
            <span className="font-medium">
              {node.codigo} · {node.nombre}
            </span>
            <span className="text-xs text-[rgb(var(--muted))]">{node.activo ? 'Activo' : 'Inactivo'}</span>
          </div>
        </button>
        <button
          type="button"
          onClick={() => onToggle(node)}
          className={`rounded-full px-3 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
            node.activo
              ? 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand-700))]'
              : 'bg-[rgba(var(--muted),0.2)] text-[rgb(var(--muted))]'
          }`}
        >
          {node.activo ? 'Activo' : 'Inactivo'}
        </button>
      </div>
      {hasChildren && expanded ? (
        <div className="mt-1 border-l border-dashed border-[rgba(var(--border),0.5)] pl-3">
          <CentroTree
            nodes={node.hijos}
            selectedId={selectedId}
            onSelect={onSelect}
            onToggle={onToggle}
            depth={depth + 1}
          />
        </div>
      ) : null}
    </li>
  );
}

